import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, X } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Input } from "./catalyst/Input";

type StringKeys<T> = {
	[K in keyof T]-?: T[K] extends string ? K : never;
}[keyof T];

type EditableKeys<T extends { id: string }> = Exclude<StringKeys<T>, "id">;

/**
 * We intentionally do not type mutationOptions.
 * oRPC owns this surface; we only pass options through.
 */
type MutationProcedure = {
	// biome-ignore lint/suspicious/noExplicitAny: we don't really care for the internals.
	mutationOptions: (options?: any) => any;
};

type FormValues = { value: string };

export function InlineEditor<T extends { id: string }>({
	id,
	field,
	currentValue,
	procedure,
	invalidateKeys,
}: {
	id: T["id"];
	field: EditableKeys<T>;
	currentValue: string;
	procedure: MutationProcedure;
	invalidateKeys?: unknown[][];
}) {
	const queryClient = useQueryClient();
	const [isEditing, setIsEditing] = useState(false);

	const { mutate, isPending } = useMutation<unknown, unknown, unknown>(
		procedure.mutationOptions({
			onSuccess: () => {
				invalidateKeys?.forEach((key) => {
					queryClient.invalidateQueries({ queryKey: key });
				});
				toast.success("Changes saved!");
				setIsEditing(false);
			},
			onError: () => {
				toast.error("Failed to save changes");
			},
		}),
	);

	const form = useForm<FormValues>({
		defaultValues: { value: currentValue },
	});

	const isDirty = form.watch("value") !== currentValue;

	if (isEditing) {
		return (
			<form
				className="flex items-center gap-2"
				onSubmit={form.handleSubmit(({ value }) => {
					if (value === currentValue) {
						setIsEditing(false);
						return;
					}
					mutate({
						id,
						[field]: value,
					});
				})}
			>
				<Input
					type="text"
					disabled={isPending}
					{...form.register("value")}
					className="inline-flex w-auto!"
				/>
				<button
					type="submit"
					aria-label={isDirty ? "Save" : "Cancel"}
					disabled={isPending}
					className="cursor-pointer"
				>
					{isDirty ? <Check className="size-4" /> : <X className="size-4" />}
				</button>
			</form>
		);
	}

	return (
		<div className="flex items-center gap-2">
			{currentValue}
			<button
				type="button"
				aria-label="Edit"
				onClick={() => setIsEditing(true)}
				className="cursor-pointer"
			>
				<Pencil className="size-3" />
			</button>
		</div>
	);
}
